import React, { useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { useStore } from "@tanstack/react-form";
import { componentFormKey } from "./ComponentTreeEditor";
import { Printer, Plus, Trash2, Sparkles } from "lucide-react";

import { toVnDateString, PLATE_PRINT_PRESETS, pressPlateSize } from "@apv-erp/status";
import { useMachineLookup } from "@/hooks/useFKLookup";
export interface CTPPlateItem {
  id: string;
  toolingId: string;
  toolingType: "PLATE";
  colorName: string;
  plateSize: string;
  machineId?: string;
  machineName: string;
  source: "INTERNAL" | "OUTSOURCE";
  /** Trạng thái ghi bản (chỉ đọc, đổi tại view lệnh: Gửi ghi bản kẽm / Ghi bản kẽm xong) */
  stage: "PENDING_CTP" | "BURNING" | "CTP_READY" | "MOUNTED";
  /** Số bản kẽm */
  quantity: number;
  /** Ngày cần (yyyy-MM-dd) */
  expectedDate?: string;
  /** Hợp phần dùng kẽm (id đã lưu hoặc clientKey hợp phần mới; trống = dùng chung) */
  componentKey?: string | null;
  notes?: string;
  /** Dòng gốc từ lệnh — giữ các trường không sửa ở bảng này khi ghi lại (mã, trạng thái sẵn sàng, ...) */
  raw?: any;
}

const PLATE_STAGE: Record<CTPPlateItem["stage"], { label: string; className: string }> = {
  PENDING_CTP: { label: "Chờ ghi bản", className: "bg-amber-50 text-amber-700 border-amber-300" },
  BURNING: { label: "Đang ghi bản", className: "bg-blue-50 text-blue-700 border-blue-300" },
  CTP_READY: { label: "Đã ghi bản xong", className: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  MOUNTED: { label: "Đã lên máy", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

// Trạng thái ghi bản lưu trong readinessStatus + thẻ [STATUS: …] ở ghi chú (giống view lệnh)
const plateStageOf = (t: any): CTPPlateItem["stage"] => {
  const tag = /\[STATUS:\s*([A-Z_]+)\]/.exec(t.notes || "")?.[1];
  if (tag === "MOUNTED") return "MOUNTED";
  if (t.readinessStatus === "READY" || tag === "CTP_READY") return "CTP_READY";
  if (tag === "BURNING" || t.readinessStatus === "PARTIAL") return "BURNING";
  return "PENDING_CTP";
};
const toDateInput = (v: any) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : toVnDateString(d);
};



export function PlatePlanBuilder({
  form,
  productionOrderId,
}: {
  form: any;
  productionOrderId?: string;
}) {
  // Khổ kẽm theo máy in (quy tắc 6): mỗi máy 1 khổ; dòng kẽm nội bộ lấy khổ của máy, không chọn tay
  const { data: machineRows } = useMachineLookup();
  const pressSizeOf = (machineId?: string) => {
    const m = (machineRows || []).find((r: any) => r.id === machineId || r.machineId === machineId);
    return m ? pressPlateSize(m) : null;
  };



  // Read toolings reactively from form store
  const rawToolings = useStore(form.store, (s: any) => s.values.toolings) || [];
  // Cây hợp phần: lệnh nhiều hợp phần thì mỗi dòng chọn hợp phần dùng (trống = dùng chung cả lệnh)
  const formComponents: any[] = (useStore(form.store, (s: any) => s.values.components) || []).filter((c: any) => !c?.isDeleted);
  const treeMode = formComponents.length > 1 || formComponents.some((c: any) => c.parentKey || c.parentComponentId);
  const components = useStore(form.store, (s: any) => s.values.components) || [];
  const printingPaperSize = useStore(form.store, (s: any) => s.values.printingPaperSize) || "";

  // Parse plate items from toolings (items with toolingType === 'PLATE' or 'ZINC')
  const plateItems: CTPPlateItem[] = useMemo(() => {
    return rawToolings
      .filter((t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC")
      .map((t: any, idx: number) => {
        // Cột riêng (plate_color, plate_size, machine_id, plate_source); bản kẽm cũ: đọc từ ghi chú dạng
        // "Màu: … | Khổ: … | Máy: … | Nguồn: … | <ghi chú>"
        let colorName = t.plateColor || t.colorName || "";
        let plateSize = t.plateSize || "";
        let machineId = t.machineId || "";
        let machineName = t.machineName || t.machine?.name || "";
        let source: "INTERNAL" | "OUTSOURCE" =
          t.plateSource === "OUTSOURCE" || t.source === "OUTSOURCE" ? "OUTSOURCE" : "INTERNAL";
        let userNotes = t.notes || "";

        if (t.notes && /Màu:|Khổ:|Máy:|Nguồn:/i.test(t.notes)) {
          const parts = String(t.notes).split("|").map((x: string) => x.trim());
          const pick = (re: RegExp) => parts.find((x: string) => re.test(x))?.replace(re, "").trim() || "";
          colorName = colorName || pick(/^Màu:\s*/i);
          plateSize = plateSize || pick(/^Khổ:\s*/i);
          machineName = machineName || pick(/^Máy:\s*/i);
          if (!t.plateSource && /Ngoài/i.test(pick(/^Nguồn:\s*/i))) source = "OUTSOURCE";
          userNotes = parts.filter((x: string) => !/^(Màu|Khổ|Máy|Nguồn):/i.test(x)).join(" | ");
        }

        return {
          id: t.id || t.toolingId || `ctp-plate-${idx}-${Date.now()}`,
          toolingId: t.toolingId || t.id || `CTP-${idx + 1}`,
          toolingType: "PLATE",
          colorName,
          plateSize,
          machineId,
          machineName,
          source,
          stage: plateStageOf(t),
          quantity: Math.max(1, Number(t.quantity) || 1),
          expectedDate: toDateInput(t.expectedDate),
          componentKey: t.componentKey ?? t.componentId ?? "",
          notes: String(userNotes).replace(/\s*\[STATUS:[^\]]+\]/g, "").trim(),
          raw: t,
        };
      });
  }, [rawToolings]);

  // Synchronize back to form.values.toolings (preserving non-plate toolings)
  const commitPlateItems = (newPlates: CTPPlateItem[]) => {
    const existingNonPlates = (form.getFieldValue("toolings") || []).filter(
      (t: any) => t.toolingType !== "PLATE" && t.toolingType !== "ZINC"
    );

    const mappedPlates = newPlates.map((p, idx) => ({
      ...(p.raw || {}),
      id: p.id || `ctp-${idx + 1}`,
      toolingId: p.toolingId || `CTP-${idx + 1}`,
      toolingType: p.raw?.toolingType === "ZINC" ? "ZINC" : "PLATE",
      status: p.raw?.status || "NEW",
      // Lưu vào cột riêng; ghi chú = nội dung người dùng nhập + thẻ trạng thái ghi bản (nếu có)
      notes: [p.notes || "", /\[STATUS:[^\]]+\]/.exec(p.raw?.notes || "")?.[0] || ""].filter(Boolean).join(" "),
      quantity: Math.max(1, Number(p.quantity) || 1),
      expectedDate: p.expectedDate || null,
      componentKey: p.componentKey || null,
      componentId: p.componentKey && !String(p.componentKey).startsWith("new-comp-") ? p.componentKey : null,
      plateColor: p.colorName,
      plateSize: p.plateSize,
      machineId: p.machineId,
      plateSource: p.source,
      colorName: p.colorName,
      machineName: p.machineName,
      source: p.source,
      isUserCustomized: true,
    }));

    const nextToolings = [...existingNonPlates, ...mappedPlates];
    form.setFieldValue("toolings", nextToolings);

    // Keep plateIds in sync
    const nextPlateIds = mappedPlates.map((p) => p.toolingId);
    form.setFieldValue("plateIds", nextPlateIds);
  };

  // Máy in gán ở công đoạn in của quy trình (không bịa tên máy khi chưa gán)
  const detectedPrintMachine = useMemo(() => {
    const mainComp = components[0];
    if (mainComp?.routingSteps && Array.isArray(mainComp.routingSteps)) {
      const printStep = mainComp.routingSteps.find((s: any) => {
        const name = (s.processName || s.name || "").toLowerCase();
        return name.includes("in") || name.includes("offset");
      });
      if (printStep) {
        return printStep.assignedMachine || printStep.machineName || printStep.workCenter || "";
      }
    }
    return "";
  }, [components]);

  const newPlate = (idx: number, colorName = ""): CTPPlateItem => ({
    id: `plate-${Date.now()}-${idx}`,
    toolingId: `CTP-P${plateItems.length + idx + 1}`,
    toolingType: "PLATE",
    colorName,
    plateSize: plateItems[0]?.plateSize || "",
    machineId: plateItems[0]?.machineId || "",
    machineName: plateItems[0]?.machineName || detectedPrintMachine,
    source: plateItems[0]?.source || "INTERNAL",
    stage: "PENDING_CTP",
    quantity: 1,
    expectedDate: plateItems[0]?.expectedDate || "",
    notes: "",
  });

  // Thêm bộ kẽm theo kiểu in (CMYK 4 lá, CMYK + 2 pha 6, 2 mặt A/B 8, tự trở 4, 2 màu 2); khổ/máy lấy theo dòng kẽm đầu
  const handleAddPreset = (key: string) => {
    const preset = PLATE_PRINT_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const generated = preset.colors.map((c, idx) => newPlate(idx, c));
    commitPlateItems([...plateItems, ...generated]);
    toast.success(`Đã thêm ${generated.length} bản kẽm: ${preset.label}`);
  };

  const handleAddPlateRow = () => {
    commitPlateItems([...plateItems, newPlate(0)]);
  };

  // Update a plate row
  const handleUpdatePlate = (index: number, updates: Partial<CTPPlateItem>) => {
    const nextList = [...plateItems];
    nextList[index] = { ...nextList[index], ...updates };
    commitPlateItems(nextList);
  };

  // Remove a plate row
  const handleRemovePlate = (index: number) => {
    const nextList = plateItems.filter((_, idx) => idx !== index);
    commitPlateItems(nextList);
    toast.info("Đã xóa bản kẽm khỏi kế hoạch");
  };

  const totalPlates = plateItems.reduce((n, p) => n + Math.max(1, Number(p.quantity) || 1), 0);

  return (
    <div className="space-y-3" data-testid="po-form-plates">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div className="text-xs text-slate-600">
          <span className="font-bold text-slate-800 inline-flex items-center gap-1.5">
            <Printer className="h-4 w-4 text-indigo-600" /> Bản kẽm CTP
          </span>
          <span className="ml-2">{plateItems.length} dòng · {totalPlates} bản</span>
          <span className="ml-2 text-slate-400">Kẽm xuất mới cho mỗi lệnh, dùng 1 lần; kẽm thuê ngoài không trừ kho kẽm.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value="" onValueChange={handleAddPreset}>
            <SelectTrigger
              className="h-8 w-auto gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium text-xs"
              data-testid="plate-preset-trigger"
            >
              <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
              <SelectValue placeholder="Thêm theo kiểu in" />
            </SelectTrigger>
            <SelectContent>
              {PLATE_PRINT_PRESETS.map((p) => (
                <SelectItem key={p.key} value={p.key} className="text-xs">
                  {p.label} ({p.colors.length} lá)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium h-8 text-xs"
            onClick={handleAddPlateRow}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Thêm bản kẽm
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="border-b border-slate-200 text-[11px]">
              <TableHead className="w-10 text-center font-bold text-slate-600">#</TableHead>
              {treeMode && <TableHead className="font-bold text-slate-600 min-w-[130px]">Hợp phần</TableHead>}
              <TableHead className="font-bold text-slate-600 min-w-[180px]">Màu / tên kẽm</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[170px]">Khổ kẽm</TableHead>
              <TableHead className="font-bold text-slate-600 w-[80px] text-right">SL</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[150px]">Máy in</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[120px]">Nguồn</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[140px]">Ngày cần</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[110px]">Trạng thái</TableHead>
              <TableHead className="font-bold text-slate-600 min-w-[140px]">Ghi chú</TableHead>
              <TableHead className="w-12 text-right pr-3 font-bold text-slate-600">Xóa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plateItems.length > 0 ? (
              plateItems.map((plate, index) => (
                <TableRow key={plate.id || index} data-testid="po-form-plate-row" className="hover:bg-slate-50/60 border-b border-slate-100">
                  <TableCell className="text-center font-mono text-xs text-slate-400">{index + 1}</TableCell>
                  {treeMode && (
                    <TableCell>
                      <select
                        aria-label="Hợp phần"
                        data-testid="po-form-tooling-component"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-200 bg-white px-1.5 text-xs"
                        value={plate.componentKey || ""}
                        onChange={(e) => handleUpdatePlate(index, { componentKey: e.target.value || "" })}
                      >
                        <option value="">Dùng chung</option>
                        {formComponents.map((c: any, ci: number) => {
                          const k = componentFormKey(c);
                          return k ? <option key={k} value={k}>{c.componentName || `Hợp phần ${ci + 1}`}</option> : null;
                        })}
                      </select>
                    </TableCell>
                  )}

                  <TableCell>
                    <ComboboxConfigMaster
                      categoryCode="ink_colors"
                      value={plate.colorName}
                      onChange={(val) => handleUpdatePlate(index, { colorName: val })}
                      placeholder="Vd: Cyan (C), Pantone 485"
                      hideLabel
                      className="h-8 text-xs font-semibold text-slate-800"
                    />
                  </TableCell>

                  <TableCell data-testid="po-form-plate-size">
                    {plate.source !== "OUTSOURCE" ? (
                      // Kẽm nội bộ: khổ theo máy in (không dùng khổ của máy này cho máy kia)
                      plate.machineId ? (
                        pressSizeOf(plate.machineId) ? (
                          <span className="text-xs font-mono font-semibold text-slate-800" title="Khổ kẽm theo máy in">
                            {pressSizeOf(plate.machineId)}
                          </span>
                        ) : (
                          <span className="text-[11px] text-rose-600">Máy chưa khai báo khổ kẽm</span>
                        )
                      ) : (
                        <span className="text-[11px] text-slate-500">Theo máy in của lệnh</span>
                      )
                    ) : (
                    // Danh mục Khổ kẽm (plate_sizes): ô chọn chung, thêm nhanh khổ mới theo quyền; khổ đã lưu ngoài danh mục vẫn hiện (03/11/2026)
                    <ComboboxConfigMaster
                      categoryCode="plate_sizes"
                      value={plate.plateSize || ""}
                      onChange={(val) => handleUpdatePlate(index, { plateSize: val })}
                      placeholder="Chọn khổ kẽm"
                      hideLabel
                      className="text-xs font-mono"
                    />
                    )}
                  </TableCell>

                  <TableCell>
                    <NumericInput
                      value={plate.quantity}
                      onChange={(e: any) => handleUpdatePlate(index, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                      className="h-8 text-xs text-right font-mono"
                      data-testid="po-form-plate-qty"
                    />
                  </TableCell>

                  <TableCell>
                    <div className="h-8 flex items-center min-w-[150px]">
                      <SharedCombobox
                        type="machine"
                        value={plate.machineId || ""}
                        valueLabel={plate.machineName}
                        onChange={(val, opt) => {
                          // Khổ kẽm của dòng nội bộ = khổ của máy in vừa chọn
                          const size = pressSizeOf(val);
                          handleUpdatePlate(index, {
                            machineId: val,
                            machineName: opt?.label || plate.machineName,
                            ...(plate.source !== "OUTSOURCE" ? { plateSize: size || "" } : {}),
                          });
                        }}
                        placeholder="Chọn máy in..."
                        hideLabel
                      />
                    </div>
                  </TableCell>

                  <TableCell>
                    <Select value={plate.source} onValueChange={(val: "INTERNAL" | "OUTSOURCE") => handleUpdatePlate(index, { source: val })}>
                      <SelectTrigger className="h-8 text-xs bg-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INTERNAL" className="text-xs">Nội bộ</SelectItem>
                        <SelectItem value="OUTSOURCE" className="text-xs">Thuê ngoài</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <Input
                      type="date"
                      value={plate.expectedDate || ""}
                      onChange={(e) => handleUpdatePlate(index, { expectedDate: e.target.value })}
                      className="h-8 text-xs bg-transparent"
                    />
                  </TableCell>

                  <TableCell>
                    <Badge variant="outline" className={`text-[11px] ${PLATE_STAGE[plate.stage].className}`}>
                      {PLATE_STAGE[plate.stage].label}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Input
                      value={plate.notes || ""}
                      onChange={(e) => handleUpdatePlate(index, { notes: e.target.value })}
                      className="h-8 text-xs text-slate-600 bg-transparent"
                      placeholder="Tram, lưu ý chế bản..."
                    />
                  </TableCell>

                  <TableCell className="text-right pr-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      onClick={() => handleRemovePlate(index)}
                      title="Xóa bản kẽm"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={treeMode ? 11 : 10} className="text-center py-8 text-xs text-slate-500">
                  Chưa có bản kẽm. Chọn "Thêm theo kiểu in" hoặc bấm "Thêm bản kẽm".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
